import type { DashboardLayoutType, PanelMetaData } from '@/api/dashboardsApi'
import type { FleetFilter } from '@/dashboard-panels/common/fleetFilters'
import type { RefreshInterval } from '@/lib/refreshIntervals'
import type { TimeRange } from '@/stores/timerange'
import { createRef } from 'react'
import { flushSync } from 'react-dom'
import { create } from 'zustand'
import type { PanelPictureInPictureHandle } from './PanelPictureInPicture'

/**
 * Which panel is in picture in picture, and the dashboard view it was opened from. Only what to show: the
 * host fetches the panel's data, device metadata and device count itself.
 */
export interface PictureInPictureEntry {
	tenantId: string
	dashboardId: string
	deviceId: string
	dashboardType: DashboardLayoutType
	panelId: string
	/** The query the panel runs, including any unsaved edit on the dashboard it came from. */
	panelMeta: PanelMetaData
	fleetFilters: FleetFilter[] | undefined
	timeRange: TimeRange
	refreshInterval: RefreshInterval
}

interface PictureInPictureState {
	entry: PictureInPictureEntry | null
	open: (entry: PictureInPictureEntry) => void
	close: () => void
}

/** One panel at a time, like a video mini player. */
export const usePictureInPictureStore = create<PictureInPictureState>()(set => ({
	entry: null,
	open: entry => set({ entry }),
	close: () => set({ entry: null }),
}))

export const pictureInPictureHandle = createRef<PanelPictureInPictureHandle>()

/**
 * Opens the panel and pops it straight out. Call it inside the click: the browser only opens the window on a
 * user gesture, so the host has to mount and pop out before the handler returns. Where it cannot, the panel
 * stays in the mini player.
 */
export function openPictureInPicture(entry: PictureInPictureEntry) {
	flushSync(() => usePictureInPictureStore.getState().open(entry))
	pictureInPictureHandle.current?.popOut()
}
